import _ from 'lodash'
import { useMemo } from 'react'
import { DocumentNode, useMutation } from '@apollo/client'
import { adminJobRequestQuery_jobRequest_jobRequestComments as MessageType } from '@OpsQuality/JobRequestDetails/__generated__/adminJobRequestQuery'
import { EligibleFollower } from '@OpsQuality/types'

import { FollowerUserDetailsInput } from '~/__generated__/globalTypes'
import { updateFollowersForJobRequest } from '../query'
import logger from '~/lib/logger'
import {
    AllowedSubjectTypes,
    useCreateJobRequestCommentMutation,
    JobRequestDetailsDocument,
    useGetEligibleFollowersQuery,
    EligibleFollowerUser,
    FollowerUser,
    DriFragment as MfgDriType,
} from '~/gql/graphql'

export const FICTIV_DOMAIN = 'fictiv.com'

export const extractMessageName = (
    message: MessageType,
    loggedUserId: number,
) => {
    const splitEmail = message.author?.email?.split('@')
    const firstName = _.get(message, 'author.firstName')
    const lastName = _.get(message, 'author.lastName')
    let showAuthorText = `${firstName} ${lastName}`
    let initial = _.get(firstName, '0')

    if (loggedUserId === message.author?.id) {
        showAuthorText = 'You'
    } else if (splitEmail && splitEmail[1] !== FICTIV_DOMAIN) {
        if (!firstName && !lastName) {
            showAuthorText = 'Manufacturing Partner'
            initial = 'MP'
        }
    } else {
        if (!firstName && !lastName) {
            showAuthorText = 'Fictiv'
        }
        initial = 'Fictiv'
    }
    return {
        showAuthorText,
        initial,
    }
}

const useFollwersSource = (dataList?: EligibleFollowerUser[] | null) => {
    return useMemo(() => {
        return (
            dataList &&
            dataList?.map(object => {
                return {
                    VALUE: object.id,
                    LABEL: `${object.firstName} ${object.lastName}`,
                    USER_ID: object.odooUserId,
                }
            })
        )
    }, [dataList])
}

export const useFollowersData = () => {
    const { data } = useGetEligibleFollowersQuery()
    const eligibleFollowers = data?.getEligibleFollowers

    return useFollwersSource(eligibleFollowers)
}

export const getFollowersObject = (
    follower: FollowerUser,
    mfgDri?: MfgDriType | null,
    eligibleFollowers?: EligibleFollower[],
) => {
    const { fictivUserId, name, id, isChannel } = follower

    return {
        id: fictivUserId,
        followerName: name,
        partnerId: id,
        assigned: fictivUserId === mfgDri?.id ? true : false,
        delete: false,
        canDelete: eligibleFollowers?.find(
            dataFollower => dataFollower?.USER_ID === id,
        )
            ? true
            : false,
        isAdded: false,
        isChannel: isChannel,
    }
}

export function MutationOperations() {
    const [updateFollowersListForJobRequest] = useMutation(
        updateFollowersForJobRequest,
    )

    const [createJobRequestCommentMutation] =
        useCreateJobRequestCommentMutation()

    return {
        updateFollowersList: async (
            jobRequestId: number,
            followerIds: FollowerUserDetailsInput[],
            deletedFollowerIds: FollowerUserDetailsInput[],
        ) => {
            try {
                const result = await updateFollowersListForJobRequest({
                    variables: {
                        jobRequestId,
                        input: {
                            addFollowers: followerIds,
                            removeFollowers: deletedFollowerIds,
                        },
                    },
                    refetchQueries: [
                        {
                            query: JobRequestDetailsDocument,
                            variables: {
                                id: jobRequestId,
                            },
                        },
                    ],
                })

                return result
            } catch (err) {
                logger.exception(err, {
                    extra: {
                        info: 'updateJobRequestFollowers::Failed to update followers for jobRequestId:  ',
                        jobRequestId,
                    },
                })
            }
        },
        createJobRequestComment: async (
            jobRequestId: string,
            body: string,
            partName: string,
            subject: string,
            attachmentIds: number[],
            query: DocumentNode,
        ) => {
            try {
                const result = await createJobRequestCommentMutation({
                    variables: {
                        jobRequestCommentInput: {
                            id: jobRequestId,
                            body,
                            partName,
                            attachmentIds,
                            subject: subject as AllowedSubjectTypes,
                        },
                    },
                    refetchQueries: [
                        {
                            query,
                            variables: { id: Number(jobRequestId) },
                        },
                    ],
                })

                return result
            } catch (err) {
                logger.exception(err, {
                    extra: {
                        info: 'createJobRequestComment::Failed to create comment for jobRequestId:  ',
                        jobRequestId,
                    },
                })
            }
        },
    }
}

export const formatFileSize = (bytes: number) => {
    if (!bytes || isNaN(bytes)) {
        return '0.00 B'
    }
    const exponent = Math.floor(Math.log(bytes) / Math.log(1024))

    return `${(bytes / Math.pow(1024, exponent)).toFixed(2)} ${' KMGTP'.charAt(
        exponent,
    )}B`
}
